import { CalendarApi, EventApi } from '@fullcalendar/react';
import {
  makeAutoObservable,
  runInAction,
} from 'mobx';
import { CalendarDateService, DateObject } from '@uplift/services';
import {
  TimeRange,
  WeekDayKeys,
  RecurringTypes,
  ProviderMedicalRole,
  CreateAppointmentMode,
} from '@uplift/types';
import { CalendarAppointmentPatient, calendarAppointments, calendarBookingSlots, userAvailabilityForRange, LegacyUICalendarEventAppointment, SlotStepByMedicalRole } from '@uplift/api';

import type {
  NewAppointment,
  CalendarModalsType,
} from '@uplift/types';
import { DateFormats } from '@uplift/constants';


type CalendarEvent = EventApi & { parentId?: string; error?: string };

const emptyAppointment: NewAppointment = {
  isNewPatient: '',
  firstName: '',
  lastName: '',
  email: '',
  time: {
    start: '',
    end: '',
  },
  notes: '',
  recurring: RecurringTypes.weekly,
};


export type CalendarEventInput = {
  id: string;
  title: string;
  start: string;
  end: string;
  extendedProps: {
    isRegular: boolean;
  };
}; // start and end are ISO datetime


export const ProviderDefaultDuration: Record<ProviderMedicalRole, number> = {
  [ProviderMedicalRole.therapist]: 60,
  [ProviderMedicalRole.psychiatrist]: 30,
};


export class CalendarStore {
  selectedEvent: Pick<CalendarEvent, 'id' | 'start' | 'end' | 'extendedProps'> | null = null;
  oldSelectedEvent: CalendarEvent | null = null;
  newEvent: NewAppointment = emptyAppointment;
  creatingEventMode = CreateAppointmentMode.existing;
  isCalendarLoading = false;
  patients: Record<string, CalendarAppointmentPatient> = {};
  appointments: LegacyUICalendarEventAppointment[] = [];
  calendarApi: CalendarApi | null = null;
  allowedSlots = new Set<string>();
  availabilityOverrides: Record<string, TimeRange[]> = {};
  availabilityTemplate: Record<WeekDayKeys, TimeRange[]> = {
    SUNDAY: [],
    MONDAY: [],
    TUESDAY: [],
    WEDNESDAY: [],
    THURSDAY: [],
    FRIDAY: [],
    SATURDAY: [],
  };
  modalsVisibility: CalendarModalsType = {
    details: false,
    noSessionOccurred: false,
    cancel: false,
    reschedule: false,
    create: false,
  };

  constructor() {
    makeAutoObservable(this, {
      _lastInfo: false,
    });
  }

  get isCreatingNewEvent(): boolean {
    return this.creatingEventMode === CreateAppointmentMode.new;
  }

  openModal = (modal: keyof CalendarModalsType) => {
    // close all modals
    (Object.keys(this.modalsVisibility) as (keyof typeof this.modalsVisibility)[]).forEach(
      (key) => (this.modalsVisibility[key] = false)
    );

    this.modalsVisibility[modal] = true;
  };

  closeModal(modal: keyof CalendarModalsType) {
    this.modalsVisibility[modal] = false;
  }

  setSelectedEvent = (event: Pick<CalendarEvent, 'id' | 'start' | 'end' | 'extendedProps'>) => {
    this.selectedEvent = event;
  };

  setNewEvent = (eventDetails: Partial<NewAppointment>) => {
    this.newEvent = { ...this.newEvent, ...eventDetails };
  };

  resetNewEvent() {
    this.newEvent = emptyAppointment;
  }

  setCreateEventMode = (mode: CreateAppointmentMode) => {
    this.creatingEventMode = mode;
  };

  setCalendarApi = (api: CalendarApi) => {
    this.calendarApi = api;
  };

  _lastInfo?: { startStr: string; endStr: string; timeZone: string };
  loadAllEvents = async (info: { startStr: string; endStr: string; timeZone: string }, force = false, showLoader = true) => {
    if (this._lastInfo?.startStr === info.startStr && this._lastInfo?.endStr === info.endStr && !force) {
      return;
    } else {
      runInAction(() => {
        if (showLoader) {
          this.isCalendarLoading = true;
        }
        this._lastInfo = info;
      });
    }

    const datesRange = {
      datetime_from: info.startStr,
      datetime_to: info.endStr
    };

    const [ availabilityResponse, appointmentsResponse, bookingSlotsResponse ] = await Promise.all([
      userAvailabilityForRange(datesRange),
      calendarAppointments(datesRange),
      calendarBookingSlots(datesRange),
    ]);

    runInAction(() => {
      this.availabilityTemplate = availabilityResponse.template;
      this.availabilityOverrides = availabilityResponse.overrides;

      this.patients = appointmentsResponse.patients;
      this.appointments = appointmentsResponse.appointments;
      this.allowedSlots = new Set(bookingSlotsResponse.available_slots);

      this.isCalendarLoading = false;
    });
  };

  isRangeAvailableForBooking = (start: DateObject, end: DateObject, slotMinutesDuration: number) => {
    const now = CalendarDateService.date();
    const slots = CalendarDateService.splitDateTime(start.utc(), end.utc(), slotMinutesDuration);

    for (const selectedDateTime of slots) {
      const isPast = selectedDateTime.diff(now, 'seconds') < 0;

      if (isPast) {
        return false;
      }

      const selectedDateTimeString = selectedDateTime.format(DateFormats.ISODayjs);
      const isSlotAllowed = this.allowedSlots.has(selectedDateTimeString);

      if (!isSlotAllowed) {
        return false;
      }
    }

    return true;
  };

  getFirstAvailableCreateAppointmentRange = (
    args: {
      start: DateObject;
      timezone: string;
      medicalRole?: ProviderMedicalRole;
    },
  ): Promise<{ start: string; end: string }> => {
    return new Promise((resolve, reject) => {
      let start = args.start
        .tz(args.timezone)
        .set('milliseconds', 0)
        .set('seconds', 0)
        .set('minutes', 0)
        .set('hour', 9)
        .utc();

      const now = CalendarDateService.date().tz(args.timezone).utc();

      if (now.isSameOrAfter(start)) {
        start = start
          .set('date', now.date())
          .set('hours', now.hour())
          .set('minutes', now.minute());
        const minutes = start.minute();

        if (minutes >= 45) {
          start = start.add(1, 'hours').set('minutes', 0);
        } else if (minutes >= 30) {
          start = start.set('minutes', 45);
        } else if (minutes >= 15) {
          start = start.set('minutes', 30);
        } else if (minutes >= 0) {
          start = start.set('minutes', 15);
        }
      }

      const slotMinutesDuration = SlotStepByMedicalRole[args.medicalRole ?? ProviderMedicalRole.therapist];
      const duration = args.medicalRole ? ProviderDefaultDuration[args.medicalRole] : ProviderDefaultDuration.therapist;
      let available = this.isRangeAvailableForBooking(start, start.add(duration, 'minutes'), slotMinutesDuration);

      for (const _slot of this.allowedSlots) {
        if (available) break;
        start = start.add(15, 'minutes');
        available = this.isRangeAvailableForBooking(start, start.add(duration, 'minutes'), slotMinutesDuration);
      }

      if (!available) reject(undefined);

      const end = duration && start.add(duration, 'minutes');

      if (start && end) {
        resolve({
          start: start.format(),
          end: end.format(),
        });
      } else {
        reject(undefined);
      }
    });
  };
}
